import { useState, useEffect, useRef } from 'react';
import './CategoriesSlider.scss';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getLanguage } from '../../utils/Helper';

const CategoriesSlider = ({ autoSlideInterval = 5000 }) => {
  const lang = getLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const authContext = useAuth();
  const navigate = useNavigate();
  const [categoriesData, setCategoriesData] = useState([]);
  
  // Touch gesture refs
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const carouselRef = useRef(null);
  const isSwiping = useRef(false);

  // Load Collection & Category
  const loadCollectionsData = () => {
    if (!authContext?.systemCollections?.length) return;
    let cats = [];
    authContext?.systemCollections.forEach(col => {
      cats = [...cats, ...col.categories.map(cat => {
        return { ...cat, collection: col.name, collection_url: col.name_url }
      })]
    });
    setCategoriesData(cats.sort((a, b) => a.id - b.id));
  }

  const goToProductsCategory = (category) => {
    const collectionName = category.collection_url.toString().toLowerCase().trim().split(" ").join("-")
    const catName = category.name_url.toString().toLowerCase().trim().split(" ").join("-")
    navigate(`/collection/${collectionName}/${catName}`);
  }

  // Navigate functions
  const goToNext = () => {
    if (isAnimating || categoriesData.length === 0) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % categoriesData.length);
    setTimeout(() => setIsAnimating(false), 800);
  };

  const goToPrev = () => {
    if (isAnimating || categoriesData.length === 0) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + categoriesData.length) % categoriesData.length);
    setTimeout(() => setIsAnimating(false), 800);
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    if (isAnimating) return;
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
    isSwiping.current = true;
  };

  const handleTouchMove = (e) => {
    if (!isSwiping.current || isAnimating) return;
    touchEndX.current = e.touches[0].clientX;
    
    // Add visual feedback during swipe
    const diff = touchStartX.current - touchEndX.current;
    if (carouselRef.current && Math.abs(diff) > 10) {
      carouselRef.current.style.transform = `translateX(${diff * 0.3}px)`;
      carouselRef.current.style.transition = 'transform 0.1s ease';
    }
  };

  const handleTouchEnd = () => {
    if (!isSwiping.current || isAnimating) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Minimum swipe distance in pixels
    
    // Reset transform
    if (carouselRef.current) {
      carouselRef.current.style.transform = 'translateX(0)';
      carouselRef.current.style.transition = 'transform 0.3s ease';
    }
    
    // Check if swipe distance is significant
    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        // Swipe left - go to next
        goToNext();
      } else {
        // Swipe right - go to previous
        goToPrev();
      }
    }
    
    isSwiping.current = false;
  };

  // Mouse drag support for desktop touch devices
  const handleMouseDown = (e) => {
    if (isAnimating || !('ontouchstart' in window)) return;
    touchStartX.current = e.clientX;
    touchEndX.current = e.clientX;
    isSwiping.current = true;
    
    // Prevent text selection during drag
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isSwiping.current || isAnimating || !('ontouchstart' in window)) return;
    touchEndX.current = e.clientX;
    
    const diff = touchStartX.current - touchEndX.current;
    if (carouselRef.current && Math.abs(diff) > 10) {
      carouselRef.current.style.transform = `translateX(${diff * 0.3}px)`;
      carouselRef.current.style.transition = 'transform 0.1s ease';
    }
  };

  const handleMouseUp = () => {
    if (!isSwiping.current || isAnimating) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;
    
    if (carouselRef.current) {
      carouselRef.current.style.transform = 'translateX(0)';
      carouselRef.current.style.transition = 'transform 0.3s ease';
    }
    
    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }
    
    isSwiping.current = false;
    
    // Remove event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Cleanup mouse events
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  });

  // Auto slide effect
  useEffect(() => {
    if (categoriesData.length === 0 || isSwiping.current) return;
    const interval = setInterval(goToNext, autoSlideInterval);
    return () => clearInterval(interval);
  }, [currentIndex, categoriesData.length, autoSlideInterval, isAnimating]);

  useEffect(() => {
    if (authContext?.systemCollections?.length) loadCollectionsData();
  }, [authContext.systemCollections]);

  // Get position for each item
  const getItemPosition = (index) => {
    const diff = index - currentIndex;
    const total = categoriesData.length;
    
    let position = diff;
    if (diff > total / 2) position = diff - total;
    if (diff < -total / 2) position = diff + total;
    
    return position;
  };

  // Calculate dynamic styles for each position
  const getItemStyle = (position) => {
    const spacing = 380;
    const absPos = Math.abs(position);
    
    if (position === 0) {
      return {
        transform: 'translate(-50%, -50%) translateX(0) translateZ(100px) scale(1.15)',
        opacity: 1,
        filter: 'brightness(1.1)',
        zIndex: 10,
        pointerEvents: 'auto'
      };
    } else if (absPos === 1) {
      const rotateY = position > 0 ? -35 : 35;
      const translateX = position > 0 ? spacing : -spacing;
      return {
        transform: `translate(-50%, -50%) translateX(${translateX}px) translateZ(-100px) rotateY(${rotateY}deg) scale(0.85)`,
        opacity: 0.7,
        filter: 'brightness(0.6)',
        zIndex: 5,
        pointerEvents: 'none'
      };
    } else if (absPos === 2) {
      const rotateY = position > 0 ? -45 : 45;
      const translateX = position > 0 ? spacing * 1.6 : -spacing * 1.6;
      return {
        transform: `translate(-50%, -50%) translateX(${translateX}px) translateZ(-250px) rotateY(${rotateY}deg) scale(0.65)`,
        opacity: 0.4,
        filter: 'brightness(0.4)',
        zIndex: 3,
        pointerEvents: 'none'
      };
    } else {
      const translateX = position > 0 ? spacing * 2.5 : -spacing * 2.5;
      return {
        transform: `translate(-50%, -50%) translateX(${translateX}px) translateZ(-400px) scale(0.4)`,
        opacity: 0,
        filter: 'brightness(0.2)',
        zIndex: 1,
        pointerEvents: 'none'
      };
    }
  };

  if (categoriesData.length === 0) {
    return <div className="slider_empty">No categories available</div>;
  }

  return (
    <div className="categories_slider_3d" style={{backgroundImage: "url('/assets/yellow background.jpg')"}}>
      
      <div className="slider_header">
        <h2 className='text-light'>{lang === "EN" ? "Browse Categories" : "تصفح أشهر الفئات لدينا"}</h2>
      </div>

      <div 
        className="carousel_container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        <div 
          className="carousel_viewport" 
          ref={carouselRef}
          style={{ cursor: isSwiping.current ? 'grabbing' : 'grab' }}
        >
          {categoriesData.map((category, index) => {
            const position = getItemPosition(index);
            const isCenter = position === 0;
            const itemStyle = getItemStyle(position);
            
            return (
              <div
                key={category.id}
                className={`carousel_item ${isCenter ? 'active' : ''}`}
                style={itemStyle}
                onClick={() => isCenter && goToProductsCategory(category)}
              >
                <div className="item_image">
                  <img
                    src={category.image_url}
                    alt={category.name}
                  />
                  <div className="image_overlay"></div>
                </div>
                
                <div className={`item_content ${isCenter ? 'show' : ''}`}>
                  <h2 className="content_tag">{category.collection}</h2>
                  <h3 className="content_title">{category.name}</h3>
                  <p className="content_subtitle">
                    {lang === "EN" ? "Adventure is never far away" : "المغامرة دائماً قريبة"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation Controls */}
        <button 
          className="carousel_control prev" 
          onClick={goToPrev}
          disabled={isAnimating}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        
        <button 
          className="carousel_control next" 
          onClick={goToNext}
          disabled={isAnimating}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* Progress Indicators */}
      <div className="carousel_indicators">
        {categoriesData.map((_, index) => (
          <button
            key={index}
            className={`indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => {
              if (!isAnimating) {
                setIsAnimating(true);
                setCurrentIndex(index);
                setTimeout(() => setIsAnimating(false), 800);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default CategoriesSlider;